import {
	createTrackerApiTrackersPost,
	getTrackersApiTrackersGet,
	getTrackerApiTrackersTrackerIdGet,
	updateTrackerApiTrackersTrackerIdPatch,
	deleteTrackerApiTrackersTrackerIdDelete,
	archiveTrackerApiTrackersTrackerIdArchivePost,
	unarchiveTrackerApiTrackersTrackerIdUnarchivePost,
	trashTrackerApiTrackersTrackerIdTrashPost,
	restoreTrackerApiTrackersTrackerIdRestorePost,
	permanentDeleteTrackerApiTrackersTrackerIdPermanentDelete,
	getArchivedTrackersApiTrackersArchivedGet,
	getTrashedTrackersApiTrackersTrashGet,
	emptyTrashApiTrackersTrashEmptyPost,
	checkTrackerHasEntriesApiTrackersTrackerIdHasEntriesGet,
	addMetricToTrackerApiTrackersTrackerIdMetricsPost,
	updateMetricApiTrackersTrackerIdMetricsMetricIdPatch,
	deleteMetricApiTrackersTrackerIdMetricsMetricIdDelete,
	reorderMetricsApiTrackersTrackerIdMetricsReorderPatch
} from './generated/api';

import type {
	TrackerCreate,
	TrackerRead,
	TrackerUpdate,
	PagedTrackerResponse,
	MetricCreate,
	MetricUpdate,
	MetricRead,
	HasEntriesResponse,
	GetTrackersApiTrackersGetParams
} from './generated/schemas';
import { createApiRequestError, isApiFailureCode } from '$lib/utils/apiErrors';
import * as m from '$lib/paraglide/messages';
import { HEALTH_CONSENT_REQUIRED } from '$lib/query/healthConsentErrors';

export type {
	TrackerCreate,
	TrackerRead,
	TrackerUpdate,
	PagedTrackerResponse,
	MetricCreate,
	MetricUpdate,
	MetricRead,
	HasEntriesResponse,
	GetTrackersApiTrackersGetParams
};

// Re-export types for backward compatibility and to use them as standard names
export type Metric = MetricRead;
export type Tracker = TrackerRead;

export interface PaginatedTrackers {
	items: Tracker[];
	total: number;
	page: number;
	size: number;
	pages: number;
}

/**
 * SML-140: a 403 `HEALTH_CONSENT_REQUIRED` keeps its stable code (with the
 * restricted-account copy) so callers can tell missing consent apart from a
 * generic failure; every other failure keeps the generic fallback.
 */
function healthConsentRequiredError() {
	return createApiRequestError(HEALTH_CONSENT_REQUIRED, m.health_consent_restricted_body());
}

function trackerRequestError(error: unknown) {
	if (isApiFailureCode(error, HEALTH_CONSENT_REQUIRED)) return healthConsentRequiredError();

	return createApiRequestError('TRACKER_REQUEST_FAILED');
}

function trackerMutationError(error: unknown) {
	if (isApiFailureCode(error, HEALTH_CONSENT_REQUIRED)) return healthConsentRequiredError();
	if (isApiFailureCode(error, 'INVALID_INPUT')) {
		return createApiRequestError('TRACKER_INVALID_INPUT', m.tracker_input_length_error());
	}

	return createApiRequestError('TRACKER_REQUEST_FAILED');
}

// Wrapper functions for better DX
export const getTracker = (id: string) => {
	if (!id) {
		throw createApiRequestError('TRACKER_REQUEST_FAILED');
	}
	return TrackerAPI.getTracker(id);
};
export const getTrackers = (page?: number, size?: number, category_id?: string) =>
	TrackerAPI.getTrackers(page, size, category_id);

// API functions for tracker detail view
export async function getTrackerEntries(
	trackerId: string,
	params: {
		start_date?: string;
		end_date?: string;
		page?: number;
		size?: number;
	}
) {
	if (!trackerId) {
		throw createApiRequestError('TRACKER_REQUEST_FAILED');
	}

	// TODO: Implement when backend endpoint is available
	// For now, return mock data
	return {
		items: [],
		total: 0,
		page: params.page || 1,
		size: params.size || 50,
		pages: 0
	};
}

export async function getTrackerStatistics(
	trackerId: string,
	params: {
		start_date?: string;
		end_date?: string;
		metric_id?: string;
	}
) {
	if (!trackerId) {
		throw createApiRequestError('TRACKER_REQUEST_FAILED');
	}

	// TODO: Implement when backend endpoint is available
	// For now, return mock statistics
	return {
		sum: 100,
		average: 10,
		max: 20,
		min: 5,
		count: 10,
		median: 10,
		std_dev: 2.5
	};
}

export class TrackerAPI {
	static async createTracker(data: TrackerCreate): Promise<Tracker> {
		try {
			const response = await createTrackerApiTrackersPost(data);
			return response as Tracker;
		} catch (error) {
			throw trackerMutationError(error);
		}
	}

	static async getTrackers(page = 1, size = 50, category_id?: string): Promise<PaginatedTrackers> {
		try {
			const params: GetTrackersApiTrackersGetParams = {
				name_filter: undefined,
				category_id: category_id || undefined,
				sort_by: 'created_at',
				sort_order: 'desc'
			};

			const response = await getTrackersApiTrackersGet(params);
			// Response is now the direct data from the API
			// Convert PagedTrackerResponse to PaginatedTrackers
			return {
				items: response.items as Tracker[],
				total: response.total || 0,
				page: page,
				size: size,
				pages: Math.ceil((response.total || 0) / size)
			};
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async getTracker(id: string): Promise<Tracker> {
		try {
			const response = await getTrackerApiTrackersTrackerIdGet(id);
			return response as Tracker;
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async updateTracker(id: string, data: TrackerUpdate): Promise<Tracker> {
		try {
			const response = await updateTrackerApiTrackersTrackerIdPatch(id, data);
			return response as Tracker;
		} catch (error) {
			throw trackerMutationError(error);
		}
	}

	static async deleteTracker(id: string): Promise<void> {
		try {
			await deleteTrackerApiTrackersTrackerIdDelete(id);
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	// Archive/Trash Methods
	static async archiveTracker(id: string): Promise<Tracker> {
		try {
			const response = await archiveTrackerApiTrackersTrackerIdArchivePost(id);
			return response as Tracker;
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async unarchiveTracker(id: string): Promise<Tracker> {
		try {
			const response = await unarchiveTrackerApiTrackersTrackerIdUnarchivePost(id);
			return response as Tracker;
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async trashTracker(id: string): Promise<Tracker> {
		try {
			const response = await trashTrackerApiTrackersTrackerIdTrashPost(id);
			return response as Tracker;
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async restoreTracker(id: string): Promise<Tracker> {
		try {
			const response = await restoreTrackerApiTrackersTrackerIdRestorePost(id);
			return response as Tracker;
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async permanentlyDeleteTracker(id: string): Promise<void> {
		try {
			await permanentDeleteTrackerApiTrackersTrackerIdPermanentDelete(id, { confirm: true });
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async getArchivedTrackers(page = 1, size = 50): Promise<PaginatedTrackers> {
		try {
			const response = await getArchivedTrackersApiTrackersArchivedGet();
			return {
				items: response.items as Tracker[],
				total: response.total || 0,
				page: page,
				size: size,
				pages: Math.ceil((response.total || 0) / size)
			};
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async getTrashedTrackers(page = 1, size = 50): Promise<PaginatedTrackers> {
		try {
			const response = await getTrashedTrackersApiTrackersTrashGet();
			return {
				items: response.items as Tracker[],
				total: response.total || 0,
				page: page,
				size: size,
				pages: Math.ceil((response.total || 0) / size)
			};
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async emptyTrash(): Promise<void> {
		try {
			await emptyTrashApiTrackersTrashEmptyPost();
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	// Metric Management Methods
	static async hasEntries(trackerId: string): Promise<boolean> {
		try {
			const response = await checkTrackerHasEntriesApiTrackersTrackerIdHasEntriesGet(trackerId);
			return response.has_entries || false;
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async addMetric(trackerId: string, metricData: MetricCreate): Promise<Metric> {
		try {
			const response = await addMetricToTrackerApiTrackersTrackerIdMetricsPost(
				trackerId,
				metricData
			);
			return response as Metric;
		} catch (error) {
			throw trackerMutationError(error);
		}
	}

	static async updateMetric(
		trackerId: string,
		metricId: string,
		metricData: MetricUpdate
	): Promise<Metric> {
		try {
			const response = await updateMetricApiTrackersTrackerIdMetricsMetricIdPatch(
				trackerId,
				metricId,
				metricData
			);
			return response as Metric;
		} catch (err) {
			if (isApiFailureCode(err, 'INVALID_INPUT')) {
				throw trackerMutationError(err);
			}
			// samlla#v757: surface the specific neutral message when a semantic
			// change (type/scale/select-option removal) is blocked by history —
			// every other failure keeps the generic fallback.
			if (isApiFailureCode(err, 'METRIC_SCHEMA_CONFLICT_HAS_HISTORY')) {
				throw createApiRequestError(
					'METRIC_SCHEMA_CONFLICT_HAS_HISTORY',
					m.metric_schema_conflict_has_history()
				);
			}
			throw trackerRequestError(err);
		}
	}

	static async deleteMetric(trackerId: string, metricId: string): Promise<void> {
		try {
			await deleteMetricApiTrackersTrackerIdMetricsMetricIdDelete(trackerId, metricId);
		} catch (error) {
			throw trackerRequestError(error);
		}
	}

	static async reorderMetrics(trackerId: string, metricIds: string[]): Promise<Metric[]> {
		try {
			const response = await reorderMetricsApiTrackersTrackerIdMetricsReorderPatch(trackerId, {
				metric_ids: metricIds
			});
			return response.metrics as Metric[];
		} catch (error) {
			throw trackerRequestError(error);
		}
	}
}
